import * as React from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popover from '@mui/material/Popover'
import { bindPopover } from 'material-ui-popup-state/hooks'
import { InputGroupSection } from 'components/UI/MultiGroupDropdown'


type LookupOption = {
  key: string
  label: string
}

type LookupGroup = {
  title: string
  options: LookupOption[]
}

type LeadTableLookupMenuProps = {
  popupState: ReturnType<typeof import('material-ui-popup-state/hooks').usePopupState>
  options: LookupOption[]
  selectedKeys: string[]
  onToggle: (key: string, checked: boolean) => void
  groups?: LookupGroup[]
}

export default function LeadTableLookupMenu({
  popupState,
  options,
  selectedKeys,
  onToggle,
  groups,
}: LeadTableLookupMenuProps) {
  const renderOption = React.useCallback(
    (option: LookupOption) => {
      const checked = selectedKeys.includes(option.key)

      return (
        <MenuItem
          key={option.key}
          value={option.key}
          onClick={(event) => {
            event.preventDefault()
            onToggle(option.key, !checked)
          }}
          sx={{
            maxHeight: 36,
            pl: '16px',
            justifyContent: 'space-between',
            width: '100%',
            '&.Mui-selected': { backgroundColor: 'white' },
          }}
        >
          <FormControlLabel
            sx={{ width: '100%', m: 0 }}
            control={
              <Checkbox
                checked={checked}
                onChange={(_event, nextChecked) => {
                  onToggle(option.key, nextChecked)
                }}
                onClick={(event) => {
                  event.stopPropagation()
                }}
                disableRipple
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              />
            }
            label={option.label}
          />
        </MenuItem>
      )
    },
    [onToggle, selectedKeys]
  )

  return (
    <Popover
      {...bindPopover(popupState)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          maxHeight: 360,
          minWidth: 260,
          overflowY: 'auto',
          py: 0.5,
        },
      }}
    >
      <MenuList dense disablePadding>
        {groups?.length
          ? groups.map((group) => (
              <Box key={group.title}>
                {group.options.length > 0 && (
                  <InputGroupSection title={group.title}>
                    {group.options.map(renderOption)}
                  </InputGroupSection>
                )}
              </Box>
            ))
          : options.map(renderOption)}
      </MenuList>
    </Popover>
  )
}
